<template>
  <div id="patients">
    <message-box v-if="showMessage" :messages="[message]" />
    <v-overlay :value="showWrapperOverlay" absolute>
      <v-progress-circular indeterminate size="64" />
    </v-overlay>
    <div v-if="patientsTable" id="patient-wrapper">
      <v-card id="patient-table">
        <h2>{{ $t("patient_list") }}</h2>
        <v-data-table
          :value="[selectedPatient]"
          :headers="patientHeaders"
          :items="patients"
          :hide-default-header="mobileDisplay"
          :footer-props="footerProps"
          @click:row="
            (selectedPatient = $event), (showPatientDetailDialog = true)
          "
        >
          <template #item.gender="{ item }">{{ $t(item.gender) }}</template>
          <template #item.birthdate="{ item }">
            {{ calculateAge(item.birthdate) }}
          </template>
          <template slot="footer.page-text" slot-scope="item">
            {{ item.pageStop }} z {{ item.itemsLength }}
          </template>
        </v-data-table>
      </v-card>
      <v-card v-if="largeDisplay" id="patient-detail">
        <div class="d-flex justify-space-between">
          <h2 class="mb-0">
            {{ $t("patient") }}
            {{ selectedPatient ? selectedPatient.initials : "" }}
          </h2>
          <div>
            <v-menu offset-y>
              <template v-slot:activator="{ on }">
                <v-btn v-on="on" icon>
                  <v-icon large>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item
                  v-for="(item, i) in items"
                  :key="i"
                  class="menu-item"
                  @click="item.action(item.params)"
                >
                  <v-list-item-title>
                    <v-icon class="mr-2">{{ item.icon }}</v-icon>
                    <div>{{ item.title }}</div>
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </div>
        <v-subheader class="pl-0">
          <div class="mr-4">{{ $t("easi_index") }}-{{ easiIndex }}</div>
          <div class="mr-4">{{ $t("severity_scale") }}: {{ easiLabel }}</div>
          <div>{{ $t("next_visit") }}: {{ nextVisit }}</div>
        </v-subheader>
        <div class="d-flex justify-space-between align-center">
          <div>
            <v-btn
              color="#415995"
              dark
              class="mb-4 mr-4"
              @click="
                $router.push({
                  name: 'grafy',
                  params: { id: selectedPatient.id },
                })
              "
              >{{ $t("show_charts") }}</v-btn
            >
            <v-btn
              color="#415995"
              dark
              class="mb-4 mr-4"
              @click="
                $router.push({
                  name: 'fotodokumentace',
                  params: { id: selectedPatient.id },
                })
              "
              >{{ $t("show_photos") }}</v-btn
            >
          </div>
          <div>
            <div class="indication-checker-header">
              {{ $t("dupixent_indication") }}:
            </div>
            <div
              v-if="selectedPatient"
              :class="[
                violatedRestriction ? 'not-suitable' : 'suitable',
                'ribbon',
              ]"
            >
              <div>
                {{
                  violatedRestriction
                    ? violatedRestriction
                    : $t("indication_fulfill")
                }}<a
                  href="https://www.sukl.cz/modules/medication/detail.php?code=0222565&tab=prices"
                  target="_blank"
                  class="ml-2"
                  >{{ $t("more_information") }}</a
                >
              </div>
            </div>
          </div>
        </div>
        <v-row no-gutters class="patient-visit-wrapper-large">
          <v-col class="patient-visits" lg="auto">
            <v-card class="patient-visits-table">
              <v-btn
                color="#415995"
                dark
                class="btn-add-visit"
                @click="
                  $router.push({
                    name: 'pridat-vyhodnoceni',
                    params: { id: selectedPatient.id },
                  })
                "
                >{{ $t("add_evaluation") }}</v-btn
              >
              <v-data-table
                :value="[selectedPatientResult]"
                :headers="patientResultHeaders"
                :items="patientForms"
                @click:row="selectedPatientResult = $event"
                :footer-props="footerProps"
              >
                <template #item.evaluated_on="{ item }">
                  {{ formatDate(item.evaluated_on) }}
                </template>
                <template #item.id="{ item }">
                  {{ patientScores[patientForms.indexOf(item)].toFixed(1) }}
                </template>
                <template slot="footer.page-text" slot-scope="item">
                  {{ item.pageStop }} z {{ item.itemsLength }}
                </template>
              </v-data-table>
            </v-card>
          </v-col>
          <v-col>
            <v-card>
              <v-data-table
                id="patient-detail-table"
                :headers="easiHeader"
                :items="patientResults"
                hide-default-footer
              >
                <template #item.type="{ item }">{{ $t(item.type) }}</template>
              </v-data-table>
            </v-card>
          </v-col>
        </v-row>
        <v-overlay :value="showPatientOverlay && !patientLoading">
          <v-progress-circular indeterminate size="64" />
        </v-overlay>
      </v-card>
      <v-dialog v-model="showPatientDetailDialog" v-else>
        <v-card id="patient-detail">
          <div class="d-flex justify-space-between">
            <h2 class="mb-0">
              {{ $t("patient") }}
              {{ selectedPatient ? selectedPatient.initials : "" }}
            </h2>
            <div>
              <div @click="showPatientDetailDialog = false">
                <i class="fal fa-times-circle fa-2x"></i>
              </div>
            </div>
          </div>
          <v-subheader class="pl-0 tablet-element">
            <div class="mr-4">{{ $t("easi_index") }}-{{ easiIndex }}</div>
            <div class="mr-4">{{ $t("severity_scale") }}: {{ easiLabel }}</div>
            <div>
              {{ $t("next_visit") }}:
              <span class="date-wrapper">{{ nextVisit }}</span>
            </div>
          </v-subheader>
          <div
            class="
              d-flex
              justify-space-between
              align-md-center
              flex-md-row flex-column
            "
          >
            <div class="d-flex">
              <v-btn
                color="#415995"
                dark
                class="mb-4 mr-4 tablet-element"
                @click="
                  $router.push({
                    name: 'grafy',
                    params: { id: selectedPatient.id },
                  })
                "
                >{{ $t("show_charts") }}</v-btn
              >
              <v-btn
                color="#415995"
                dark
                class="mb-4 tablet-element"
                @click="
                  $router.push({
                    name: 'fotodokumentace',
                    params: { id: selectedPatient.id },
                  })
                "
                >{{ $t("show_photos") }}</v-btn
              >
              <div>
                <v-menu offset-y>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      class="tablet-element mx-4"
                      color="#415995"
                      dark
                      v-on="on"
                    >
                      {{ $t("more_option") }}
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item
                      v-for="(item, i) in items"
                      :key="i"
                      class="menu-item"
                    >
                      <v-list-item-title @click="item.action(item.params)">
                        <v-icon class="mr-2">{{ item.icon }}</v-icon>
                        <div>{{ item.title }}</div>
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </div>
            <div>
              <div class="indication-checker-header">
                {{ $t("dupixent_indication") }}:
              </div>
              <div
                v-if="selectedPatient"
                :class="[
                  violatedRestriction ? 'not-suitable' : 'suitable',
                  'ribbon',
                ]"
              >
                <div class="mr-2">
                  {{
                    violatedRestriction
                      ? violatedRestriction
                      : "Pacient splňuje podmínky indikačního omezení."
                  }}
                </div>
                <a
                  href="https://www.sukl.cz/modules/medication/detail.php?code=0222565&tab=prices"
                  target="_blank"
                  >Více informací</a
                >
              </div>
            </div>
          </div>
          <v-row no-gutters class="patient-visit-wrapper">
            <v-col class="patient-visits" :cols="smallDisplay ? 12 : 'auto'">
              <v-card class="patient-visits-table">
                <v-btn
                  color="#415995"
                  dark
                  class="btn-add-visit"
                  @click="
                    $router.push({
                      name: 'pridat-vyhodnoceni',
                      params: { id: selectedPatient.id },
                    })
                  "
                  >{{ $t("add_evaluation") }}</v-btn
                >
                <v-data-table
                  class="mobile-table"
                  :mobile-breakpoint="900"
                  :value="[selectedPatientResult]"
                  :headers="patientResultHeaders"
                  :items="patientForms"
                  :hide-default-header="mobileDisplay"
                  :footer-props="footerProps"
                  @click:row="(selectedPatientResult = $event), clickToScroll()"
                >
                  <template #item.evaluated_on="{ item }">
                    {{ formatDate(item.evaluated_on) }}
                  </template>
                  <template #item.id="{ item }">
                    {{ patientScores[patientForms.indexOf(item)].toFixed(1) }}
                  </template>
                  <template slot="footer.page-text" slot-scope="item">
                    {{ item.pageStop }} z {{ item.itemsLength }}
                  </template>
                </v-data-table>
              </v-card>
            </v-col>
            <v-col>
              <h3 id="visit-detail" class="mobile-element pt-8">
                {{ $t("visit_detail") }}: {{ selectedPatientResult.visit }}
              </h3>
              <v-subheader class="pl-0 mobile-element">
                <div class="mr-4">{{ $t("easi_index") }}-{{ easiIndex }}</div>
                <div class="mr-4">
                  {{ $t("severity_scale") }}: {{ easiLabel }}
                </div>
                <div>
                  {{ $t("next_visit") }}:
                  <span class="date-wrapper">{{ nextVisit }}</span>
                </div>
              </v-subheader>
              <v-btn
                color="#415995"
                dark
                class="mb-4 mr-4 mobile-element"
                @click="
                  $router.push({
                    name: 'grafy',
                    params: { id: selectedPatient.id },
                  })
                "
                >{{ $t("show_charts") }}</v-btn
              >
              <v-btn
                color="#415995"
                dark
                class="mb-4 mobile-element"
                @click="
                  $router.push({
                    name: 'fotodokumentace',
                    params: { id: selectedPatient.id },
                  })
                "
                >{{ $t("show_photos") }}</v-btn
              >
              <div>
                <v-menu offset-y>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      class="mobile-element"
                      color="#415995"
                      dark
                      v-on="on"
                    >
                      {{ $t("more_option") }}
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item
                      v-for="(item, i) in items"
                      :key="i"
                      class="menu-item"
                    >
                      <v-list-item-title @click="item.action(item.params)">
                        <v-icon class="mr-2">{{ item.icon }}</v-icon>
                        <div>{{ item.title }}</div>
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
              <v-data-table
                id="patient-detail-table"
                class="mobile-table"
                :mobile-breakpoint="900"
                :headers="easiHeader"
                :items="patientResults"
                :hide-default-header="mobileDisplay"
                hide-default-footer
              >
                <template #item.type="{ item }">{{ $t(item.type) }}</template>
              </v-data-table>
            </v-col>
          </v-row>
        </v-card>
      </v-dialog>
      <v-dialog
        v-model="showPatientRemoveDialog"
        v-if="selectedPatient"
        max-width="400"
      >
        <v-card id="delete-patient-modal">
          <v-card-title>{{ $t("removing_patient") }}</v-card-title>
          <div class="dialog-text">
            {{ $t("remove_confirm_message") + ` ${selectedPatient.initials}?` }}
          </div>
          <v-card-actions class="d-flex justify-end">
            <v-btn class="mr-3" @click="showPatientRemoveDialog = false" text>{{
              $t("no")
            }}</v-btn>
            <v-btn
              :loading="removePatientLoading"
              @click="onPatientRemove"
              color="#415995"
              text
              >{{ $t("yes") }}</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
      <email-modal
        v-model="showSendEmailDiaolog"
        :patientIntials="selectedPatient.initials"
        :loading="sendEmailLoading"
        @close-dialog="showSendEmailDiaolog = false"
        @sendEmail="sendEmail"
      />
    </div>
    <div v-if="noPatients">
      <v-card id="empty-placeholder">
        <div class="text-center">
          <h2>{{ $t("no-patient") }}</h2>
        </div>
        <v-img
          src="../assets/no-data.png"
          width="400"
          class="ma-auto"
          eager
        ></v-img>
        <p class="text-center">
          {{ $t("no-patient-text") }}
        </p>
        <div class="text-center mt-4">
          <v-btn
            @click="$router.push({ name: 'kalkulator' })"
            text
            color="#415995"
            >{{ $t("move_to_calculator") }}</v-btn
          >
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
import moment from "moment";

import tokenMixin from "@/mixins/tokenMixin.js";
import MessageBox from "@/components/MessageBox.vue";
import EmailModal from "@/components/EmailModal.vue";

export default {
  name: "Patients",
  mixins: [tokenMixin],
  components: { MessageBox, EmailModal },
  data() {
    return {
      showPatientDetailDialog: false,
      patientHeaders: [
        { text: this.$t("initials"), value: "initials", sortable: false },
        { text: this.$t("gender"), value: "gender", sortable: false },
        { text: this.$t("age"), value: "birthdate", sortable: false },
      ],
      patientResultHeaders: [
        {
          text: "Týden",
          value: "visit",
          sortable: false,
        },
        {
          text: this.$t("date_of_recording"),
          value: "evaluated_on",
          sortable: false,
        },
        { text: this.$t("easi_score"), value: "id", sortable: false },
      ],
      easiHeader: [
        { text: this.$t("type"), value: "type" },
        { text: this.$t("erytem"), value: "erythema" },
        { text: this.$t("papules"), value: "papula_plaque" },
        { text: this.$t("excoriation"), value: "excoriation" },
        { text: this.$t("lichenification"), value: "lichenification" },
        { text: this.$t("eczema_range"), value: "eczema_extent" },
      ],
      footerProps: {
        "items-per-page-all-text": this.$t("all"),
        "items-per-page-text": this.$t("row_on_page"),
      },
      patients: [],
      selectedPatient: null,
      patientParams: null,
      patientForms: [],
      patientResults: [],
      patientReports: [],
      selectedPatientResult: null,
      visit: null,
      visitDate: null,
      multiplier: {
        head_neck: 0.1,
        upper_limbs: 0.2,
        torso: 0.3,
        lower_limbs: 0.4,
      },
      patientScores: [],
      easiResult: 0,
      easiLabel: this.$t("healed_alt"),
      window: {
        width: 0,
        height: 0,
      },
      showMessage: false,
      showPatientOverlay: false,
      patientLoading: false,
      showPatientRemoveDialog: false,
      showSendEmailDiaolog: false,
      removePatientLoading: false,
      sendEmailLoading: false,
      email: null,
      message: "",
      showWrapperOverlay: true,
      allPatientsResolvedPromisesCounter: 0,
      showingState: "blank",
      violatedRestriction: "",
      items: [
        {
          title: this.$t("edit_data"),
          icon: "mdi-pencil",
          action: this.redirectToPage,
        },
        {
          title: this.$t("send_report"),
          icon: "mdi-email",
          action: this.displayDialog,
          params: { propertyName: "showSendEmailDiaolog" },
        },
        {
          title: this.$t("remove_patient"),
          icon: "mdi-delete",
          action: this.displayDialog,
          params: { propertyName: "showPatientRemoveDialog" },
        },
      ],
    };
  },
  created() {
    window.addEventListener("resize", this.handleResize);
    this.handleResize();
  },
  destroyed() {
    window.removeEventListener("resize", this.handleResize);
  },
  async mounted() {
    await this.fetchPatients();
    if (this.$route.params.add === "evaluation") {
      this.showMessage = true;
      setTimeout(() => {
        this.showMessage = false;
      }, 50000);
      this.message = this.$t("record_added");
    }
  },
  watch: {
    async selectedPatient() {
      if (this.selectedPatient) {
        await this.fetchPatientDetail();
        const patientResults = [];
        this.patientReports = [];
        this.patientForms.forEach((form, index) => {
          patientResults.push({
            evaluatedOn: form.evaluated_on,
            score: this.patientScores[index],
          });
        });
        this.$router
          .push({ params: { userId: this.selectedPatient.id } })
          .catch((err) => {
            err;
          });
        this.patientForms.forEach((form, index) => {
          this.patientReports.push({
            id: form.id,
            visit: `${this.$t("week")} ${form.visit}`,
            easi_score: this.patientScores[index].toFixed(1),
            severity: this.calculateSeverity(this.patientScores[index]),
          });
        });
        this.violatedRestriction = this.checkSUKLRestrictions(
          this.calculateAge(this.selectedPatient.birthdate),
          patientResults
        );
      }
    },
    selectedPatientResult() {
      if (this.selectedPatientResult) {
        this.$router
          .push({ params: { formId: this.selectedPatientResult.id } })
          .catch((err) => {
            err;
          });
        this.patientResults = this.selectedPatientResult.body_parts;
        this.easiResult = this.patientScores[
          this.patientForms.indexOf(this.selectedPatientResult)
        ].toFixed(1);
        this.visitDate = this.patientForms[
          this.patientForms.indexOf(this.selectedPatientResult)
        ].evaluated_on;
        this.calculateVisitWeek(this.visitDate);
      } else {
        this.patientResults = [];
      }
    },
    easiResult() {
      if (this.easiResult) {
        if (this.easiResult <= 0) {
          this.easiLabel = this.$t("healed_alt");
        } else if (this.easiResult > 0 && this.easiResult <= 1) {
          this.easiLabel = this.$t("almost_healed_alt");
        } else if (this.easiResult > 1.1 && this.easiResult <= 7) {
          this.easiLabel = this.$t("moderate_alt");
        } else if (this.easiResult > 7.1 && this.easiResult <= 21) {
          this.easiLabel = this.$t("medium_alt");
        } else if (this.easiResult > 21 && this.easiResult <= 50) {
          this.easiLabel = this.$t("sevete_alt");
        } else if (this.easiResult > 50 && this.easiResult <= 72) {
          this.easiLabel = this.$t("very_severe_alt");
        }
      }
    },
    allPatientsResolvedPromisesCounter() {
      if (this.allPatientsResolvedPromisesCounter === 2) {
        this.showWrapperOverlay = false;
        if (this.patients.length === 0) {
          this.showingState = "noPatients";
        } else {
          this.showingState = "patients";
        }
      }
    },
  },
  computed: {
    largeDisplay() {
      return this.window.width >= 1375;
    },
    smallDisplay() {
      return this.window.width <= 1112;
    },
    mobileDisplay() {
      return this.window.width <= 899;
    },
    blankPage() {
      return this.showingState === "blank";
    },
    noPatients() {
      return this.showingState === "noPatients";
    },
    patientsTable() {
      return this.showingState === "patients";
    },
    nextVisit() {
      const firstVisitDate = this.patientForms[this.patientForms.length - 1]
        ?.evaluated_on;
      if (this.visit < 16) {
        const secondVisitFrom = moment(firstVisitDate).add(16, "weeks");
        const secondVisitTo = moment(firstVisitDate)
          .add(17, "weeks")
          .subtract(1, "day");
        return `${this.formatDate(secondVisitFrom)} - ${this.formatDate(
          secondVisitTo
        )}`;
      } else if (this.visit >= 16 && this.visit < 24) {
        const thirdVisitFrom = moment(firstVisitDate).add(24, "weeks");
        const thirdVisitTo = moment(firstVisitDate)
          .add(25, "weeks")
          .subtract(1, "day");
        return `${this.formatDate(thirdVisitFrom)} - ${this.formatDate(
          thirdVisitTo
        )}`;
      } else {
        return this.$t("at_least_every_24");
      }
    },
    easiIndex() {
      const firstEasiScore = this.patientScores[this.patientScores.length - 1];
      const easiIndexNumber =
        ((firstEasiScore - this.easiResult) / firstEasiScore) * 100;
      return easiIndexNumber.toFixed(0);
    },
  },
  methods: {
    handleResize() {
      this.window.width = window.innerWidth;
      this.window.height = window.innerHeight;
    },
    async fetchPatients() {
      setTimeout(() => this.allPatientsResolvedPromisesCounter++, 700);
      try {
        await this.$_checkTokenExpiration();
        ({
          body: { patients: this.patients },
        } = await this.$http.get("patients"));
        this.patients.sort((a, b) => (a.initials > b.initials ? 1 : -1));

        if (this.patients.length > 0) {
          const patientIndex = this.patients.findIndex(
            (patient) => patient.id == this.$route.params.userId
          );
          patientIndex == -1
            ? (this.selectedPatient = this.patients[0])
            : (this.selectedPatient = this.patients[patientIndex]);
        }
      } finally {
        this.allPatientsResolvedPromisesCounter++;
      }
    },
    async fetchPatientDetail() {
      this.showPatientOverlay = true;
      setTimeout(() => (this.showPatientOverlay = false), 700);
      try {
        this.patientLoading = true;
        await this.$_checkTokenExpiration();
        ({
          body: { forms: this.patientForms },
        } = await this.$http.get(`patients/${this.selectedPatient.id}`));
        this.patientScores = [];
        if (this.patientForms.length > 0) {
          const formIndex = this.patientForms.findIndex(
            (form) => form.id == this.$route.params.formId
          );
          formIndex == -1
            ? (this.selectedPatientResult = this.patientForms[0])
            : (this.selectedPatientResult = this.patientForms[formIndex]);

          this.patientForms.forEach((form, index) => {
            const bodyPartScores = [];
            form.body_parts.forEach((bodyPart) => {
              const bodyPartScore =
                (bodyPart.erythema +
                  bodyPart.excoriation +
                  bodyPart.lichenification +
                  bodyPart.papula_plaque) *
                bodyPart.eczema_extent *
                this.multiplier[bodyPart.type];
              bodyPartScores.push({
                type: bodyPart.type,
                scores: bodyPartScore,
              });
            });
            this.calculateVisitWeek(this.patientForms[index].evaluated_on);
            this.patientForms[index].visit = this.visit;
            let initialValue = 0;
            this.patientScores.push(
              bodyPartScores.reduce(
                (accumulator, currentValue) =>
                  accumulator + currentValue.scores,
                initialValue
              )
            );
          });
        } else {
          this.selectedPatientResult = null;
          this.easiResult = 0;
        }
      } finally {
        this.patientLoading = false;
      }
    },
    async onPatientRemove() {
      try {
        this.removePatientLoading = true;
        await this.$http.delete(`patients/${this.selectedPatient.id}`);
        this.showMessage = true;
        setTimeout(() => {
          this.showMessage = false;
        }, 5000);
        this.message = "Pacient byl úspěšně odstraněn";
        this.patients.splice(
          this.patients.findIndex(
            (patient) => patient.id === this.selectedPatient.id
          ),
          1
        );
        if (this.patients.length > 0) {
          this.selectedPatient = this.patients[0];
        } else {
          this.selectedPatient = null;
          this.showingState = "noPatients";
        }
      } finally {
        this.removePatientLoading = false;
        this.showPatientRemoveDialog = false;
        this.showPatientDetailDialog = false;
      }
    },
    async sendEmail(emailAdress) {
      await this.$_checkTokenExpiration();
      try {
        this.sendEmailLoading = true;
        await this.$http.post(
          `patients/${this.selectedPatient.id}/forms/send-score`,
          {
            recipient_email: emailAdress,
            forms: this.patientReports,
          }
        );
        this.showSendEmailDiaolog = false;
        this.showMessage = true;
        setTimeout(() => {
          this.showMessage = false;
        }, 5000);
        this.message = this.$t("repot_is_sent");
      } catch {
        // eslint-disable-next-line no-console
        console.error("Connection error");
      } finally {
        this.sendEmailLoading = false;
      }
    },
    calculateAge(birthDate) {
      const momentBirthDate = moment(birthDate);
      return moment.duration(moment().diff(momentBirthDate)).years();
    },
    formatDate(date) {
      return moment(date).format("DD. MM. YYYY");
    },
    clickToScroll() {
      let element = document.getElementById("visit-detail");
      setTimeout(() => {
        element.scrollIntoView({ behavior: "smooth" });
      });
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    },
    calculateVisitWeek(currentVisitDate) {
      const firstVisitDate = this.patientForms[this.patientForms.length - 1]
        ?.evaluated_on;
      const weekDiffrence = moment(currentVisitDate).diff(
        moment(firstVisitDate),
        "week"
      );
      this.visit = weekDiffrence;
    },
    checkVisit(firstVisit, currentVisit) {
      const weeksFirstCurrentVisit = moment(currentVisit.evaluatedOn).diff(
        moment(firstVisit.evaluatedOn),
        "week"
      );

      const firstVisitRound = Math.round(firstVisit.score * 10) / 10;
      const currentVisitRound = Math.round(currentVisit.score * 10) / 10;

      if (weeksFirstCurrentVisit < 15) {
        return "";
      }

      if (weeksFirstCurrentVisit === 16) {
        return currentVisitRound <= firstVisitRound * 0.5
          ? ""
          : this.$t("indication_week_16");
      }

      if (weeksFirstCurrentVisit > 16 && weeksFirstCurrentVisit < 24) {
        return this.patientForms.find((form) => {
          return form.visit === 16;
        })
          ? ""
          : this.$t("missing_visit_16");
      }
      if (weeksFirstCurrentVisit === 24) {
        return currentVisitRound <= firstVisitRound * 0.25
          ? ""
          : this.$t("indication_week_24");
      }
      return this.$t("Pacient nesplňuje podmínky indikačního omezení.");
    },
    checkVisitAfter24Week(firstVisit, currentVisit) {
      const firstVisitRound = Math.round(firstVisit.score * 10) / 10;
      const currentVisitRound = Math.round(currentVisit.score * 10) / 10;

      return currentVisitRound <= firstVisitRound * 0.5
        ? ""
        : this.$t("indication_after_24");
    },
    checkSUKLRestrictions(patientAge, patientEvaluations) {
      if (patientAge > 12) {
        if (patientEvaluations.length === 0) return this.$t("missing_records");
        if (patientEvaluations[patientEvaluations.length - 1].score < 7.1)
          return this.$t("AD_is_not_severe");

        let after24Week = false;

        if (this.patientForms[0].visit > 25) after24Week = true;

        if (after24Week === false) {
          return this.checkVisit(
            patientEvaluations[patientEvaluations.length - 1],
            patientEvaluations[0]
          );
        } else {
          const indexOf24WeekVisit = this.patientForms.findIndex(
            (element) => element.visit === 24
          );

          for (let i = indexOf24WeekVisit; i < patientEvaluations.length; i++) {
            if (i < 3) {
              return this.$t("missing_visits");
            }
            if (i % 3 === 0) {
              return this.checkVisitAfter24Week(
                patientEvaluations[patientEvaluations.length - 1],
                patientEvaluations[0]
              );
            } else {
              return this.checkVisitAfter24Week(
                patientEvaluations[patientEvaluations.length - 1],
                patientEvaluations[i % 3]
              );
            }
          }
        }
      } else {
        return this.$t("younger_12");
      }
    },
    displayDialog(params) {
      this[params.propertyName] = true;
    },
    redirectToPhotos() {
      this.$router.push({
        name: "fotodokumentace",
        params: {
          id: this.selectedPatient.id,
        },
      });
    },
    redirectToPage() {
      this.$router.push({
        name: "upravit-vyhodnoceni",
        params: {
          id: this.selectedPatient.id,
          formId: this.selectedPatientResult.id,
        },
      });
    },
    calculateSeverity(score) {
      if (score <= 0) {
        return this.$t("healed_alt");
      } else if (score > 0 && score <= 1) {
        return this.$t("almost_healed_alt");
      } else if (score > 1.1 && score <= 7) {
        return this.$t("moderate_alt");
      } else if (score > 7.1 && score <= 21) {
        return this.$t("medium_alt");
      } else if (score > 21 && score <= 50) {
        return this.$t("sevete_alt");
      } else if (score > 50 && score <= 72) {
        return this.$t("very_severe_alt");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#empty-placeholder {
  margin-top: 2rem;
  width: 40rem;
  margin: auto;
}

.v-card {
  padding: 2rem;
  box-shadow: 0px 0px 18px 0px #d1d1d1;
}

.v-btn {
  box-shadow: none;
}

#patient-wrapper {
  display: flex;
  padding: 2rem;
}

h2 {
  margin-bottom: 1rem;
}

.btn-add-visit {
  width: 100%;
  margin-bottom: 1rem;
  box-shadow: none;
}

::v-deep .v-data-table td {
  white-space: nowrap;
}

.add {
  .v-subheader {
    height: 1rem;
  }
}

.mobile-element {
  display: none;
}

.indication-checker-header {
  color: #807265;
}

.ribbon {
  padding: 0.5rem 1rem;
  display: flex;
  align-items: center;
  width: auto;
  height: 10%;
  margin: 0rem -2rem 1rem 0rem;
  color: white;
  position: relative;
  justify-content: center;
  border-radius: 0.5rem 0 0 0.5rem;
}

.suitable {
  background: #50c56f1c;
  color: #00a0a0;
  border: 1px solid #00a0a0;
  border-right: 0;
}

.not-suitable {
  background: #d878001f;
  color: #ff7600;
  border: 1px solid #ff7600;
  border-right: 0;
}

.send-email-btn {
  height: 100%;
  background: #324c8d;
}

#patient-detail {
  box-shadow: 0px 0px 18px 0px #d1d1d1;
  width: 100%;
  height: 100%;

  .v-card {
    box-shadow: none;
  }
}

.patient-visits {
  border-right: 1px solid gainsboro;
}

.patient-visit-wrapper-large {
  border-top: 1px solid gainsboro;
  flex-wrap: nowrap;
}

.v-dialog {
  .patient-visit-wrapper {
    border-bottom: 1px solid gainsboro;
  }
  .add {
    margin-top: 2rem;
  }
}

.patient-visits-table {
  width: 17.5rem;
  padding-left: 0;

  ::v-deep .v-data-table td {
    cursor: pointer;
  }

  ::v-deep .v-data-table__selected {
    color: #415995;
  }
}

#patient-table {
  margin-right: 1rem;
  ::v-deep .v-data-table td {
    cursor: pointer;
  }

  ::v-deep .v-data-table__selected {
    color: #415995;
  }
}

#delete-patient-modal {
  padding: 0;
  background: #f5f5f5;

  .dialog-text {
    padding: 2rem;
    background: white;
  }
}

.fa-times-circle {
  color: gray;
}

.v-menu__content {
  right: 4rem !important;
  left: auto !important;
  margin-top: 0.5rem;
}

.menu-item {
  cursor: pointer;

  .v-list-item__title {
    display: flex;
    align-items: center;
  }

  &:hover {
    background: rgb(231, 231, 231);
  }
}

@media only screen and (min-width: 1375px) {
  #patient-table {
    width: 17.5rem;
  }
}
@media only screen and (max-width: 1475px) and (min-width: 1375px) {
  #patient-detail {
    .v-card {
      padding: 2rem 1rem;
    }
  }
}

@media screen and (max-width: 1112px) {
  .patient-visits-table {
    width: 100%;
    padding: 2rem 0;
  }

  .patient-visits {
    border-right: none;
  }
}

@media only screen and (max-width: 1374px) {
  #patient-wrapper {
    flex-direction: column;
  }

  .v-menu__content {
    left: 14.6rem !important;
    right: auto !important;
    margin-top: 0.5rem;
  }
}

@media only screen and (max-width: 1374px) and (min-width: 960px) {
  #app-wrapper .v-menu__content--fixed {
    left: 14.5rem !important;
    right: auto !important;
    margin-top: 0.5rem;
    z-index: 2002 !important;
  }
}

@media only screen and (max-width: 900px) {
  #patient-wrapper {
    padding: 16px 5px;
  }

  .indication-checker-header {
    margin-top: 1rem;
  }

  .patient-visits-table {
    padding: 0;
  }

  #patient-table {
    margin: 0;
  }

  .mobile-table {
    border: 1px solid gainsboro;
    margin-bottom: 2rem;
  }
}

@media screen and (max-width: 400px) {
  .v-subheader {
    .date-wrapper {
      display: block;
    }
  }
}

@media only screen and (max-width: 960px) {
  ::v-deep .v-dialog {
    margin: 0;
  }

  .mobile-element {
    display: block;

    &.v-btn {
      width: 100%;
      margin-bottom: 1rem;
    }

    &.v-subheader {
      margin: 1rem 0 3rem 0;
    }
  }

  .tablet-element {
    display: none;
  }

  .ribbon {
    margin: 0 0 1rem 0;
    border-radius: 0.5rem;
    border-right: 1px solid;
    justify-content: start;
    flex-wrap: wrap;
  }
}
</style>
